import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Checkbox,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Input,
  Image,
  CloseButton
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorRepository, Vendor } from 'api/vendorRepository';
import { z } from 'zod';
import { vendorSchema, flattenZodErrors } from 'validations/Validations';
import axios from 'axios';
import { useAuth } from 'contexts/authContext';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function AddVendorForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const { role, user } = useAuth();
  // const mode = role === 'OrgAdmin' ? 'assign' : 'create';
  const location = useLocation();
  const mode =
    role === 'OrgAdmin' && location.state?.assign ? 'assign' : 'create';
  const logoInputRef = useRef<HTMLInputElement>(null);
  const attachmentsInputRef = useRef<HTMLInputElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  // Vendor data
  const [vendorData, setVendorData] = useState({
    name: '',
    contactEmail: '',
    contactNumber: '',
    organization: '',
    // status: '',
    gsTin: '',
    address: {
      locationName: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertStatus, setCenterAlertStatus] = useState<'success' | 'error' | 'warning'>('success');
  const [centerAlertMessage, setCenterAlertMessage] = useState('');

  const showCenteredAlert = (message: string, status: 'success' | 'error' | 'warning', redirect = false) => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => {
      setCenterAlertVisible(false);
      if (redirect) {
        navigate('/admin/vendors/VendorList');
      }
    }, 1500);
  };

  useEffect(() => {
    if (mode === 'assign') {
      vendorRepository.getUnassignedVendors().then(setVendors);
    }
  }, [mode]);

  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setVendorData((prev) => ({
      ...prev,
      [name]: value,
    }));

    //Field-level validation using Zod schema
    const fieldSchema = vendorSchema.shape[name as keyof typeof vendorData];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [name]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };


  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVendorData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));

    // Field-level validation for nested address schema
    const fieldSchema = vendorSchema.shape.address.shape[name as keyof typeof vendorData.address];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [`address.${name}`]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      setErrors({});
      if (mode === 'assign') {
        if (selectedVendorIds.length === 0) {
          setErrors({ vendorIds: 'Please select at least one vendor' });
          return;
        }

        setIsSubmitting(true);
        try {
          await vendorRepository.assignVendorsToOrgBulk(selectedVendorIds);

          showCenteredAlert(
            `${selectedVendorIds.length} vendor(s) assigned successfully`,
            'success',
            true
          );
        } catch (error) {
          showCenteredAlert('Failed to assign vendors', 'error');
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      vendorSchema.parse(vendorData);

      const payload: Vendor = {
        _id: '',
        name: vendorData.name,
        contactEmail: vendorData.contactEmail,
        contactNumber: vendorData.contactNumber,
        organization: vendorData.organization,
        status: 'Active',
        gsTin: vendorData.gsTin,

        logoFile: logoFile || undefined,
        attachments: attachments,


        address: {
          locationName: vendorData.address.locationName,
          street: vendorData.address.street,
          city: vendorData.address.city,
          state: vendorData.address.state,
          zipCode: vendorData.address.zipCode,
          country: vendorData.address.country,
        },
      };

      await vendorRepository.addVendor(payload);

      showCenteredAlert('Vendor added successfully!', 'success', true);

      showCenteredAlert('Vendor added successfully!', 'success', true);
    } catch (error: unknown) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        // showCenteredAlert('Please fill all required fields', 'warning');
      } else if (axios.isAxiosError(error)) {
        const backendMessage = error.response?.data || 'Failed to add vendor.';
        if (backendMessage.includes('GSTIN')) {
          setErrors((prev) => ({ ...prev, gsTin: backendMessage }));
          showCenteredAlert(backendMessage, 'error');
        } else if (backendMessage.includes('Vendor name')) {
          setErrors((prev) => ({ ...prev, name: backendMessage }));
          showCenteredAlert(backendMessage, 'error');
        } else {
          showCenteredAlert(backendMessage, 'error');
        }
      } else {
        showCenteredAlert('Failed to add vendor.', 'error');
      }
    }
    finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/vendors/VendorList');
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      w="100%"
      overflow="hidden"
      align="center"
      justify="flex-start"
      position="relative"
      px={{ base: '20px', md: '40px' }}
      pb="60px"
    >
      <Box
        position="absolute"
        top="90px"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        p="30px"
        mt={{ md: '150px' }}
        mx="auto"
        zIndex={1}
        overflowY="auto"
        borderRadius="16px"
      >
        <Flex justify="space-between" align="center" mb="20px">
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            {mode === 'assign' ? 'Assign Vendor' : 'Add Vendor'}
          </Text>

          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/vendors/VendorList')}
          />
        </Flex>

        <Flex direction="column" w="100%">
          {mode === 'assign' ? (
            <>
              <FormControl isInvalid={!!errors.vendorIds}>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Select Vendors
                </Text>
                {/* <Menu closeOnSelect={false} placement="bottom-start"> below change */}
                <Menu closeOnSelect={false} placement="bottom-start" matchWidth>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    w="100%"
                    textAlign="left"
                    variant="outline"
                  >
                    {selectedVendorIds.length > 0
                      ? `${selectedVendorIds.length} vendor(s) selected`
                      : 'Select vendors'}
                  </MenuButton>

                  <Portal>
                    <MenuList p={3} w="100%" zIndex={1500}>
                      {/*Search*/}
                      <Input
                        placeholder="Search vendors..."
                        size="sm"
                        mb={2}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      {/* Select All */}
                      <Checkbox
                        mb={2}
                        isChecked={
                          filteredVendors.length > 0 &&
                          filteredVendors.every(v =>
                            selectedVendorIds.includes(v.vendorId || v._id)
                          )
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedVendorIds(
                              filteredVendors.map(v => v.vendorId || v._id)
                            );
                          } else {
                            setSelectedVendorIds([]);
                          }
                        }}
                      >
                        Select All
                      </Checkbox>

                      {/*SCROLLABLE LIST */}
                      <Box maxH="240px" overflowY="auto">
                        {filteredVendors.length === 0 && (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                            No vendors found
                          </Text>
                        )}

                        {filteredVendors.map((vendor) => {
                          const id = vendor.vendorId || vendor._id;
                          return (
                            <MenuItem key={id} closeOnSelect={false}>
                              <Checkbox
                                w="100%"
                                isChecked={selectedVendorIds.includes(id)}
                                onChange={(e) => {
                                  setSelectedVendorIds((prev) =>
                                    e.target.checked
                                      ? [...prev, id]
                                      : prev.filter((v) => v !== id)
                                  );
                                }}
                              >
                                {vendor.name}
                              </Checkbox>
                            </MenuItem>
                          );
                        })}
                      </Box>

                    </MenuList>
                  </Portal>
                </Menu>
                <FormErrorMessage>{errors.vendorIds}</FormErrorMessage>
              </FormControl>
              <Flex justify="center" gap={3} mt={6}>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmit}
                  // isDisabled={!selectedVendorIds || isSubmitting}
                  isDisabled={selectedVendorIds.length === 0 || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Add Vendors
                </Button>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </Flex>
            </>
          ) : (
            /* ================= ADMIN UI================= */
            <>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                <Stack direction="column" gap="6px">
                  <FormControl isInvalid={!!errors['name']}>
                    <InputField
                      name="name"
                      id="name"
                      placeholder="Enter vendor name"
                      label="Vendor Name"
                      value={vendorData.name}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>{errors['name']}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors['contactNumber']}>
                    <InputField
                      name="contactNumber"
                      id="contactNumber"
                      placeholder="Enter contact number"
                      label="Contact Number"
                      value={vendorData.contactNumber}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>{errors['contactNumber']}</FormErrorMessage>
                  </FormControl>
                  {role === "Vendor" && (
                    <FormControl position="relative">
                      <Text fontSize="sm" fontWeight="bold" mb="8px">
                        Vendor Logo
                      </Text>

                      <Box
                        border="1px solid"
                        borderColor="gray.200"
                        _dark={{ borderColor: "whiteAlpha.300" }}
                        borderRadius="12px"
                        p="6px"
                        display="flex"
                        alignItems="center"
                        onClick={() => logoInputRef.current?.click()}
                        cursor="pointer"
                      >
                        <Button
                          variant="setup"
                          size="sm"
                          height="32px"
                          borderRadius="8px"
                          pointerEvents="none"
                          mr="10px"
                        >
                          Choose File
                        </Button>
                        <Text fontSize="sm" color="gray.500" noOfLines={1}>
                          {logoFile ? logoFile.name : "No file chosen"}
                        </Text>
                        <Input
                          ref={logoInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          display="none"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setLogoFile(e.target.files[0]);
                            }
                          }}
                        />
                      </Box>
                      <FormHelperText>Max 5MB. JPG/JPEG/PNG only.</FormHelperText>

                      {logoFile && (
                        <Box position="relative" display="inline-block" mt={2}>
                          <Image
                            src={URL.createObjectURL(logoFile)}
                            alt="Logo Preview"
                            maxH="120px"
                            objectFit="contain"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                          />
                          <CloseButton
                            size="sm"
                            position="absolute"
                            top="4px"
                            right="4px"
                            onClick={() => {
                              setLogoFile(null);
                            }}
                          />
                        </Box>
                      )}
                    </FormControl>
                  )}
                </Stack>

                <Stack direction="column" gap="6px">
                  <FormControl isInvalid={!!errors['contactEmail']}>
                    <InputField
                      name="contactEmail"
                      id="contactEmail"
                      placeholder="Enter contact email"
                      label="Contact Email"
                      value={vendorData.contactEmail}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>{errors['contactEmail']}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors['gsTin']}>
                    <InputField
                      name="gsTin"
                      id="gsTin"
                      placeholder="Enter GSTIN"
                      label="GSTIN"
                      value={vendorData.gsTin}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>{errors['gsTin']}</FormErrorMessage>

                    {role === "Vendor" && (
                      <FormControl>
                        <Text fontSize="sm" fontWeight="bold" mb="8px">
                          Vendor Documents
                        </Text>
                        <Box
                          border="1px solid"
                          borderColor="gray.200"
                          _dark={{ borderColor: "whiteAlpha.300" }}
                          borderRadius="12px"
                          p="6px"
                          display="flex"
                          alignItems="center"
                          onClick={() => attachmentsInputRef.current?.click()}
                          cursor="pointer"
                        >
                          <Button
                            variant="setup"
                            size="sm"
                            height="32px"
                            borderRadius="8px"
                            pointerEvents="none"
                            mr="10px"
                          >
                            Choose Files
                          </Button>
                          <Text fontSize="sm" color="gray.500" noOfLines={1}>
                            {attachments.length > 0 ? `${attachments.length} files` : "No file chosen"}
                          </Text>
                          <Input
                            ref={attachmentsInputRef}
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf"
                            display="none"
                            onChange={(e) => {
                              if (e.target.files) {
                                const files = Array.from(e.target.files);
                                if (files.length + attachments.length > 5) {
                                  showCenteredAlert("Maximum 5 files allowed", "warning");
                                  return;
                                }
                                setAttachments((prev) => [...prev, ...files]);
                              }
                            }}
                          />
                        </Box>

                        <FormHelperText>
                          Max 5 files. JPG, PNG, PDF allowed.
                        </FormHelperText>
                        {attachments.length > 0 && (
                          <Stack mt={3} spacing={3}>
                            {attachments.map((file, index) => {
                              const fileURL = URL.createObjectURL(file);
                              const isImage = file.type.startsWith("image/");
                              const isPDF = file.type === "application/pdf";

                              return (
                                <Box
                                  key={index}
                                  border="1px solid"
                                  borderColor="gray.200"
                                  _dark={{ borderColor: "whiteAlpha.300" }}
                                  borderRadius="md"
                                  p={2}
                                  position="relative"
                                >
                                  {/* FILE NAME */}
                                  <Text fontSize="sm" mb={2}>
                                    {file.name}
                                  </Text>

                                  {/* IMAGE PREVIEW */}
                                  {isImage && (
                                    <Image
                                      src={fileURL}
                                      alt={file.name}
                                      maxH="150px"
                                      objectFit="contain"
                                      borderRadius="md"
                                    />
                                  )}

                                  {/* PDF PREVIEW */}
                                  {isPDF && (
                                    <Box border="1px solid" borderColor="gray.300" borderRadius="md" overflow="hidden">
                                      <iframe
                                        src={fileURL}
                                        title={file.name}
                                        width="100%"
                                        height="200px"
                                      />
                                    </Box>
                                  )}

                                  {/* REMOVE BUTTON */}
                                  <CloseButton
                                    size="sm"
                                    position="absolute"
                                    top="6px"
                                    right="6px"
                                    onClick={() => {
                                      setAttachments((prev) =>
                                        prev.filter((_, i) => i !== index)
                                      );
                                    }}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </FormControl>
                    )}
                  </FormControl>
                </Stack>


                <Text
                  color={textColor}
                  fontSize="lg"
                  fontWeight="600"
                  mb="5px"
                  gridColumn={{ base: 'span 1', md: 'span 2' }}
                >
                  Address Details
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" gridColumn={{ base: 'span 1', md: 'span 2' }}>
                  <Stack direction="column" gap="6px">
                    <FormControl isInvalid={!!errors['address.locationName']}>
                      <InputField
                        name="locationName"
                        id="locationName"
                        placeholder="Address Line"
                        label="Address Line"
                        value={vendorData.address.locationName}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage >{errors['address.locationName']}</FormErrorMessage>
                    </FormControl>


                    <FormControl isInvalid={!!errors['address.city']}>
                      <InputField
                        name="city"
                        id="city"
                        placeholder="City"
                        label="City"
                        value={vendorData.address.city}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage>{errors['address.city']}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors['address.zipCode']}>
                      <InputField
                        name="zipCode"
                        id="zipCode"
                        placeholder="Zip Code"
                        label="Zip Code"
                        value={vendorData.address.zipCode}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage>{errors['address.zipCode']}</FormErrorMessage>
                    </FormControl>

                  </Stack>
                  <Stack direction="column" gap="6px">
                    <FormControl isInvalid={!!errors['address.street']}>
                      <InputField
                        name="street"
                        id="street"
                        placeholder="Street"
                        label="Street"
                        value={vendorData.address.street}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage>{errors['address.street']}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors['address.state']}>
                      <InputField
                        name="state"
                        id="state"
                        placeholder="State"
                        label="State"
                        value={vendorData.address.state}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage>{errors['address.state']}</FormErrorMessage>
                    </FormControl>


                    <FormControl isInvalid={!!errors['address.country']}>
                      <InputField
                        name="country"
                        id="country"
                        placeholder="Country"
                        label="Country"
                        value={vendorData.address.country}
                        onChange={handleAddressChange}
                      />
                      <FormErrorMessage>{errors['address.country']}</FormErrorMessage>
                    </FormControl>
                  </Stack>
                </SimpleGrid>
              </SimpleGrid>
              <Flex justify="center" gap="20px" mt="24px">
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  Submit
                </Button>
                <Button
                  variant="outline"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  borderColor="gray.300"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      </Card>





      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}